import React, { useState } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getSummaryReportData, saveSummaryReportData } from '../../api/SummaryReport';
import DateSelector from '../../components/DateSelector';
import { ConfirmModal, ReadyModal, WaitModal } from '../../components/ReportModals';
import TrafficLights from '../../components/TrafficLights';
import { ISummaryReportData } from '../../util/DataModelInterface';
import { getPreviousMonth, IMonthValue } from '../../util/DateUtils';
import SummaryReportTable, { ISummaryReportTableMetrics, ISummaryReportTableRow, ISummaryReportTableTotals, ISummaryReportTableLamps } from './SummaryReportTable';

interface ISummaryReportOptionsProps {
  intl: any;
}

const SummaryReport = (props: ISummaryReportOptionsProps) => {
  const [modalWaitOpen, setWaitModalOpen] = useState(false);
  const [modalSaveOpen, setSaveModalOpen] = useState(false);
  const [modalConfOpen, setConfirmationModalOpen] = useState(false);
  const [modalReadyOpen, setReadyModalOpen] = useState(false);
  const [date, setDate] = useState(getPreviousMonth());
  const [tableData, setTableData] = useState([] as ISummaryReportTableRow[]);
  const [tableTotals, setTableTotals] = useState({} as ISummaryReportTableTotals);
  const [tableMetrics, setTableMetrics] = useState({} as ISummaryReportTableMetrics);
  const [tableLamps, setTableLamps] = useState({} as ISummaryReportTableLamps);
  const [showAlert, setShowAlert] = useState(false);
  const [alertTextKey, setAlertTextKey] = useState('');
  const [rawData, setRawData] = useState({} as ISummaryReportData);
  const [calculated, setCalculated] = useState(false);

  const getReport = async () => {
    setTableData({} as ISummaryReportTableRow[]);
    setTableTotals({} as ISummaryReportTableTotals);
    setTableMetrics({} as ISummaryReportTableMetrics);
    setTableLamps({} as ISummaryReportTableLamps);
    setWaitModalOpen(true);
    setShowAlert(false);
    setCalculated(false);
    getSummaryReportData(date).then((data) => {
      setRawData(data);
      if (!data || !data.REPORTROWS) {
        if (data.error && data.error.errorID) {
          setAlertTextKey(`summaryreport.error.${data.error.errorID}`);
        } else {
          setAlertTextKey('summaryreport.noreportsfound');
        }
        setShowAlert(true);
        return;
      }
      const metrics = {
        containers: data.METRICS.CONTAINERS,
        handling: data.METRICS.HANDLING,
        receiving: data.METRICS.RECEIVING,
        transport: data.METRICS.TRANSPORT
      };
      const totals = {
        containers: parseFloat(data.TOTALS.CONTAINERS),
        handling: parseFloat(data.TOTALS.HANDLING),
        receiving: parseFloat(data.TOTALS.RECEIVING),
        transport: parseFloat(data.TOTALS.TRANSPORT),
        kg: parseFloat(data.TOTALS.KG),
        eur: 0,
        eurKg: 0,
        fee: parseFloat(data.TOTALS.FEE),
        pomKg: parseFloat(data.TOTALS.POM_KG)
      };
      let codeKey = Object.keys(data.LAMPS)[0];
      const lamps = {
        name: "19WEEE" + codeKey.charAt(0),
        codes: codeKey,
        description: "LAMPS",
        fee_fi: parseFloat(data.LAMPS[codeKey].VAL_FI),
        fee_al: parseFloat(data.LAMPS[codeKey].VAL_AL),
        pomPcs_fi: parseFloat(data.LAMPS[codeKey].KPL_FI),
        pomPcs_al: parseFloat(data.LAMPS[codeKey].KPL_AL)
      }
      setCalculated(data.REPORTEXISTS);
      // read REPORTROWS to a map and group it according to REPORTGROUPING
      const rows = {} as {[key: string]: ISummaryReportTableRow};
      const jsonRows = data.REPORTROWS;
      Object.keys(data.REPORTGROUPING).forEach((groupKey: string) => {
        const group = data.REPORTGROUPING[groupKey];
        Object.keys(group).forEach((key: string) => {
          group[key].forEach((subclass: string) => {
            const rowKey = groupKey + key;
            const eur = parseFloat(jsonRows[subclass].TOTAL_EUR);
            if (!rows[rowKey]) {
              rows[rowKey] = {
                name: groupKey,
                codes: [subclass],
                description: rowKey, // include also weee info
                eur,
                kg: parseFloat(jsonRows[subclass].KG),
                eurKg: parseFloat(jsonRows[subclass]['EUR/KG']),
                containers: parseFloat(jsonRows[subclass].CONTAINERS_EUR),
                handling: parseFloat(jsonRows[subclass].HANDLING_EUR),
                receiving: parseFloat(jsonRows[subclass].RECEIVING_EUR),
                transport: parseFloat(jsonRows[subclass].TRANSPORT_EUR),
                fee: parseFloat(jsonRows[subclass].FEE),
                pomKg: parseFloat(jsonRows[subclass].POM_WEIGHT)
              };
            } else {
              rows[rowKey].codes.push(subclass);
              rows[rowKey].kg += parseFloat(jsonRows[subclass].KG);
              rows[rowKey].eur += eur;
              rows[rowKey].containers += parseFloat(jsonRows[subclass].CONTAINERS_EUR);
              rows[rowKey].handling += parseFloat(jsonRows[subclass].HANDLING_EUR);
              rows[rowKey].receiving += parseFloat(jsonRows[subclass].RECEIVING_EUR);
              rows[rowKey].transport += parseFloat(jsonRows[subclass].TRANSPORT_EUR);
              rows[rowKey].fee += parseFloat(jsonRows[subclass].FEE);
              rows[rowKey].pomKg += parseFloat(jsonRows[subclass].POM_WEIGHT);
              rows[rowKey].eurKg = rows[rowKey].kg > 0 ?
                rows[rowKey].eur/rows[rowKey].kg : 0;
            }
            totals.eur += eur;
          })
        })
      });
      totals.eurKg = totals.eur / totals.kg;

      const table = [] as ISummaryReportTableRow[];
      Object.keys(rows).forEach((key) => table.push(rows[key]));
      setTableLamps(lamps);
      setTableMetrics(metrics);
      setTableTotals(totals);
      setTableData(table);
    }).finally(() => {
      setWaitModalOpen(false);
    });
  }

  const reset = () => {
    setTableData([] as ISummaryReportTableRow[]);
    setTableMetrics({} as ISummaryReportTableMetrics);
    setTableTotals({} as ISummaryReportTableTotals);
    setRawData({} as ISummaryReportData);
    setTableLamps({} as ISummaryReportTableLamps);
    setShowAlert(false);
  }

  const save = () => {
    setSaveModalOpen(true);
    saveSummaryReportData(date, rawData).then((data) => {
      if (!data || data.error) {
        if (data.error.errorID) {
          setAlertTextKey(`summaryreport.error.${data.error.errorID}`);
        } else {
          setAlertTextKey('summaryreport.error');
        }
        setShowAlert(true);
      } else {
        setReadyModalOpen(true);
        setTimeout(() => setReadyModalOpen(false), 2000);
        reset();
      }
    }).finally(() => setSaveModalOpen(false));
  }
  
  return (
    <div>
      <WaitModal
        messageId='summaryreport.getting'
        modalOpen={modalWaitOpen}
        setModalOpen={(open: boolean) => setWaitModalOpen(open)}/>
      <WaitModal
        messageId='summaryreport.saving'
        modalOpen={modalSaveOpen}
        setModalOpen={(open: boolean) => setSaveModalOpen(open)}/>
      <ConfirmModal
        modalOpen={modalConfOpen}
        labelId={'summaryreport.exists'}
        messageId={'summaryreport.exists.text'}
        modalText={''}
        submitId={'summaryreport.save'} dismissId={'summaryreport.reset'}
        onDismiss={() => { setConfirmationModalOpen(false); } }
        onSubmit={() => { setConfirmationModalOpen(false); save(); } } />
      <ReadyModal
        messageId="sortsummaryreports.save.ready"
        modalOpen={modalReadyOpen}
        setModalOpen={(open: boolean) => setReadyModalOpen(open)}
      />
      <h1>
        <FormattedMessage id="summaryreport.header"/>
      </h1>

      <div className="search-wrap">
        <Row className="d-flex flex-row">
          <Col>
          <Form.Group>
            <label><FormattedMessage id="summaryreport.reportmonth"/></label>
            <DateSelector startPeriod={date} 
                          onChange={(start: IMonthValue) => { setDate(start); reset(); } } 
                          dateFormat={'yyyy-MM'} />
            </Form.Group>
            <h4><FormattedMessage id="summaryreport.reportdata"/></h4>
            <TrafficLights type = {'all'} report = {'monthly'} period = {date}/>
          </Col>
        </Row>
        <Row>
          <Col className="form-buttons d-flex flex-row">
            <button className="send-button btn btn-primary ml-auto"
                    onClick={() => { getReport(); }}>
              <FormattedMessage id="summaryreport.calculate"/>
            </button>
          </Col>
        </Row>
        </div>
      { showAlert &&
        <div className="alert-wrap">
          <Alert variant="warning" onClose={() => setShowAlert(false)} dismissible={true}>
            <FormattedMessage id={alertTextKey} />
          </Alert>
        </div>
      }
      { tableData && tableData.length > 0 &&
        <SummaryReportTable
          data={tableData}
          metrics={tableMetrics}
          totals={tableTotals}
          reportDate={date}
          lamps={tableLamps}
          onReset={() => reset()}
          onSave={() => { if(!calculated) save(); else setConfirmationModalOpen(true); }}
        />
      }
    </div>
  )
}

export default injectIntl(SummaryReport);
